import { useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "./auth-context";
import { AuthLayout } from "./auth-layout";
import { SocialAuthButtons } from "./social-auth";
import { LEGAL_VERSION, PRIVACY_URL, TOS_URL } from "./legal";
import { Alert, Button, Input } from "@/components/ui";
import { ApiError } from "@/api/client";

export function RegisterPage() {
  const { t } = useTranslation();
  const { status, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Post-auth destination (e.g. an invite link). Internal paths only.
  const nextParam = params.get("next");
  const next = nextParam && nextParam.startsWith("/") ? nextParam : "/app";
  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [dob, setDob] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (status === "authenticated") return <Navigate to={next} replace />;

  const onUsername = (v: string) =>
    setUsername(v.toLowerCase().replace(/[^a-z0-9_]/g, ""));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError(t("Password must be at least 8 characters."));
      return;
    }
    if (username && !/^[a-z0-9_]{3,30}$/.test(username)) {
      setError(t("Username must be 3–30 characters: lowercase letters, digits, or underscore."));
      return;
    }
    if (!dob) {
      setError(t("Please enter your date of birth."));
      return;
    }
    if (!agreed) {
      setError(t("Please accept the Terms and Privacy Policy to continue."));
      return;
    }
    setSubmitting(true);
    try {
      await register({
        email,
        password,
        display_name: displayName.trim() || undefined,
        username: username || undefined,
        date_of_birth: dob, // YYYY-MM-DD
        tos_version_accepted: LEGAL_VERSION,
        privacy_version_accepted: LEGAL_VERSION,
      });
      // Fresh account → guided onboarding (which finishes into the app).
      navigate("/onboarding", { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setError(t("You must be at least 13 years old to use EduPause."));
      } else {
        setError(
          err instanceof Error ? err.message : t("Something went wrong. Please try again."),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title={t("Create your account")}
      subtitle={t("Start turning study time into screen time.")}
      footer={
        <>
          {t("Already have an account?")}{" "}
          <Link to="/login" className="font-semibold text-brand-600 hover:underline">
            {t("Sign in")}
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        {error && <Alert>{error}</Alert>}
        <Input
          label={t("Display name")}
          name="display_name"
          autoComplete="name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder={t("Alex")}
          hint={t("Shown to friends and classmates.")}
        />
        <Input
          label={t("Username")}
          name="username"
          autoComplete="username"
          value={username}
          onChange={(e) => onUsername(e.target.value)}
          placeholder={t("e.g. ada_99")}
          hint={t("3–30 characters: lowercase letters, digits, or underscore.")}
        />
        <Input
          label={t("Email")}
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
        />
        <Input
          label={t("Password")}
          name="password"
          type="password"
          autoComplete="new-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={t("At least 8 characters")}
        />
        <Input
          label={t("Date of birth")}
          name="date_of_birth"
          type="date"
          autoComplete="bday"
          required
          value={dob}
          onChange={(e) => setDob(e.target.value)}
          hint={t("You must be at least 13 to use EduPause.")}
        />
        <label className="flex items-start gap-2 text-xs text-ink-faint">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-black/20"
          />
          <span>
            {t("I have read and agree to the")}{" "}
            <a href={TOS_URL} target="_blank" rel="noreferrer" className="font-semibold text-brand-600 underline">
              {t("Terms of Service")}
            </a>{" "}
            {t("and")}{" "}
            <a href={PRIVACY_URL} target="_blank" rel="noreferrer" className="font-semibold text-brand-600 underline">
              {t("Privacy Policy")}
            </a>
            .
          </span>
        </label>
        <Button type="submit" size="lg" loading={submitting} className="w-full">
          {t("Create account")}
        </Button>
      </form>
      <SocialAuthButtons next="/onboarding" onError={setError} />
    </AuthLayout>
  );
}
